import { setProperties } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type Model from '@ember-data/model';
import type { Errors as DSErrors } from '@ember-data/model/-private';
import { recordIdentifierFor } from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import type { StructuredDataDocument } from '@warp-drive/core/types/request';
import type { SingleResourceDataDocument } from '@warp-drive/core/types/spec/document';

import type { CreateRecordProperties, RecordAttributes, Service } from 'webapp-types';
import { copyObject, isAlive } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import Buffer from 'webapp/models-custom/buffer/buffer';
import Validator from 'webapp/models-custom/validator';

import type { EDRequestError } from 'webapp/services/request';

// buffered props: the record's attributes and id; for a union of records (e.g. board | board-preview), those of any member
type BufferedKey<R extends Model> = R extends Model ? keyof RecordAttributes<R> | 'id' : never;
type BufferedValue<R extends Model, K> = R extends Model
	? K extends keyof R
		? R[K]
		: never
	: never;
// record attributes to set directly (directPropUpdate)
export type DirectProps<R extends Model> = {
	[K in Exclude<BufferedKey<R>, 'id'>]?: BufferedValue<R, K>;
};

export default abstract class BufferedModel<
	RecordType extends Model,
	SerializedType extends object = Record<string, unknown>,
> extends Validator {
	@service('store') declare storeService: Service.Store;
	@service('request') declare requestService: Service.Request;
	@service('request-queue') declare requestQueueService: Service.RequestQueue;
	@service declare logger: Service.Logger;
	@service declare garbageCollector: Service.GarbageCollector;

	@tracked activeFilter: string[] = []; // for filter highlighting
	@tracked animationClass = ''; // Used for animations in lazy-loading

	model: RecordType;

	_buffer: Buffer;

	abstract get id(): string | null;

	get uniqueId() {
		// for using in list/auto-loaded
		return this.idForSure;
	}

	get isDirty(): boolean {
		return this._buffer.hasBufferedChanges;
	}

	get isNew(): boolean {
		return !this.model.id;
	}

	get idForSure(): string {
		// use only to silence TypeScript
		// only in cases where we are SURE the model is saved and has id
		return this.id!;
	}

	get numericId(): number | null {
		return Number(this.id) || null;
	}

	get numericIdForSure(): number {
		// see comment at idForSure
		return +this.idForSure;
	}

	get isFake(): boolean {
		return (this.numericId ?? 0) < 0;
	}

	get dirtyProperties(): string[] {
		return this._buffer.bufferedProperties;
	}

	get modelName(): string {
		return (this.constructor as typeof BufferedModel).modelName;
	}

	get serializer(): ApplicationSerializer {
		return this.storeService.serializerFor(this.modelName);
	}

	constructor(opts: { model: RecordType }) {
		super();

		this.model = opts.model;
		this._buffer = new Buffer(opts.model);
		this._updateBackEndErrors();
	}

	getBuffered<K extends BufferedKey<RecordType>>(prop: K): BufferedValue<RecordType, K> {
		return this._buffer.getBuffered(prop) as BufferedValue<RecordType, K>;
	}

	setBuffered<K extends BufferedKey<RecordType>>(
		prop: K,
		value: BufferedValue<RecordType, K>,
	): BufferedValue<RecordType, K> {
		return this._buffer.setBuffered(prop, value) as BufferedValue<RecordType, K>;
	}

	// normalized json, obj with props like in the ED record (does not go through serializer)
	// used for e.g. making record copies (model-manager::createNewRecord) or other logic
	toJSON(includeId = false, keepUndefined = false): CreateRecordProperties<RecordType> {
		if (!isAlive(this.model)) {
			return {};
		}
		const typeClass = this.model.constructor as unknown as ModelSchema,
			normalizedData = this.serializer.normalize(
				typeClass,
				this.model.serialize() as Record<string, unknown>,
			);

		const json = normalizedData.data.attributes as Record<string, unknown>;

		// go through model props and add (possible) local model changes
		this.dirtyProperties.forEach((key: string) => {
			json[key] = Reflect.get(this, key); // same as this[key]
		});

		// add the properties that were removed in serializing
		this.serializer.getReadOnlyFields(json).forEach((key: string) => {
			json[key] = Reflect.get(this, key); // same as this[key]
		});

		if (includeId) {
			json.id = this.model.id;
		}

		let replacer;
		if (keepUndefined) {
			replacer = function (_key: string, value: unknown) {
				return typeof value === 'undefined' ? null : value;
			};
		}

		// make a copy bcos of non-primitive types
		return JSON.parse(JSON.stringify(json, replacer)) as CreateRecordProperties<RecordType>;
	}

	// serialized json, obj used in FE <-> BE communication (goes through serializer)
	// used for e.g. creating manual payloads for custom requests that do not go through ED store
	toSerializedJSON(includeId = false): SerializedType {
		const tmpModel = this.storeService.createRecord(
			this.modelName,
			this.toJSON() as Record<string, unknown>,
		) as RecordType;

		let json = tmpModel.serialize() as Record<string, unknown>;

		// make a copy bcos of non-primitive types
		json = copyObject(json);

		if (includeId) {
			json.id = this.model.id;
		}

		tmpModel.unloadRecord();

		return json as SerializedType; // built by the model's serializer
	}

	// for ED requests
	onRequestFail(dsError: EDRequestError | DSErrors | Record<string, never>) {
		let dataToLog: SerializedType | undefined;
		try {
			dataToLog = this.toSerializedJSON(true);
		} catch (_e) {
			// nothing
		}
		return this.requestService.onEDRequestFail(dsError, dataToLog);
	}

	save(force = false): Promise<RecordType> {
		const onSuccess = (model: RecordType): Promise<RecordType> => {
			return Promise.resolve(model);
		};

		const onFail = (
			dsError: EDRequestError | DSErrors | Record<string, never>,
		): Promise<never> => {
			return this.onRequestFail(dsError);
		};

		if (this.isDeletedState()) {
			// if was deleted inbetween
			return onFail({});
		}

		if (!force && !this.isDirty && !this.isNew) {
			// nothing to save
			return onSuccess(this.model);
		}

		if (this.errorsArray.length) {
			return onFail(this.model.errors);
		}

		this.applyBufferedChanges();

		return this.requestQueueService
			.add(this.model)
			.then(onSuccess, onFail)
			.finally(() => {
				this._updateBackEndErrors();
			});
	}

	saveAndCatch(force = false): Promise<RecordType | void> {
		return this.save(force).catch(
			this.requestService.showAllValidationErrors.bind(this.requestService),
		);
	}

	deleteAndCatch(): Promise<unknown> {
		return this.delete().catch(
			this.requestService.showAllValidationErrors.bind(this.requestService),
		);
	}

	reload(): Promise<RecordType> {
		// query single record again
		const onSuccess = (model: RecordType) => {
			return Promise.resolve(model);
		};

		const onFail = (dsError: DSErrors) => {
			return this.onRequestFail(dsError);
		};

		if (!this.isNew && !this.isDeletedState()) {
			return this.model.reload().then(onSuccess, onFail);
		}

		return Promise.resolve(this.model);
	}

	isPropertyDirty(property: string): boolean {
		return this.dirtyProperties.includes(property);
	}

	directPropUpdate(props: DirectProps<RecordType>): void {
		if (!this.isDeletedState()) {
			// set directly on the model
			this._buffer.discardBufferedChanges(Object.keys(props));
			setProperties(this.model, props as Partial<RecordType>);
			this.markAsClean();
		}
	}

	applyBufferedChanges(onlyTheseProps?: string[]): void {
		if (!this.isDeletedState()) {
			this._buffer.applyBufferedChanges(onlyTheseProps);
		}
	}

	markAsClean(): void {
		// using when setting properties directly on store model bcos of custom api endpoints
		// f.ex. datawall reposition
		if (!this.isNew) {
			const identifier = recordIdentifierFor(this.model),
				cache = this.storeService.cache;
			if (cache) {
				cache.willCommit(identifier, null); // the JSON:API cache ignores the context
				// an empty commit result only marks the record as clean
				cache.didCommit(identifier, {
					content: {},
					request: { op: '' },
				} as StructuredDataDocument<SingleResourceDataDocument>);
			}
		}
	}

	rollback(): boolean {
		// if model has errors, reset data to previous values
		if (!this.isNew && this.model.rollbackAttributes) {
			this.model.rollbackAttributes();
		}

		this.errors = {};

		if (this.isDirty) {
			this._buffer.discardBufferedChanges();
			return true;
		}

		return false;
	}

	getDataForCopy(): CreateRecordProperties<RecordType> {
		// can and should be overwritten in the model
		// if some props should be cleared when creating a copy
		// fallback to `toJSON`
		return this.toJSON();
	}

	// model.currentState.stateName is not reliable to observe / depend on
	isInFlight(): boolean {
		let state: string | undefined;
		try {
			state = this.model.currentState.stateName;
		} catch (_e) {
			// skip
		}
		return !!state && state.includes('inFlight');
	}

	isDeletedState(): boolean {
		let state: string | undefined;
		try {
			state = this.model.currentState.stateName;
		} catch (_e) {
			// skip
		}
		return !!state && state.includes('deleted');
	}

	delete(skipBE = false, skipClearConnectedRecords = false): Promise<unknown> {
		const modelData = this.toJSON(true);

		const promise = new Promise((resolve, reject) => {
			const onSuccess = (): Promise<void> => {
				return Promise.resolve();
			};

			const onFail = (dsError: DSErrors) => {
				return this.onRequestFail(dsError);
			};

			const resolveWrapper = (): void => {
				next(ApplicationSerializer, ApplicationSerializer.unloadLocalRecord, this.model);

				if (!skipClearConnectedRecords) {
					const garbageCollector = this.garbageCollector;
					if (garbageCollector) {
						garbageCollector.clearConnectedRecords(this.modelName, modelData);
					}
				}

				resolve(null);
			};

			const rejectWrapper = (dsError: DSErrors): void => {
				if (!this.isNew) {
					next(
						ApplicationSerializer,
						ApplicationSerializer.undeleteLocalRecord,
						this.model,
					);
				}
				reject(dsError);
			};

			if (!this.isNew) {
				ApplicationSerializer.markRecordAsIgnored(this.model);
			}

			let deletingPromise;
			if (this.isDeletedState()) {
				deletingPromise = Promise.resolve();
			} else {
				ApplicationSerializer.removeInFlightState(this.model);

				if (skipBE || this.isNew) {
					try {
						this.model.deleteRecord();
					} catch (_e) {
						// sometimes throws an error for setting isDeleted on undefined but it works fine
					}
					deletingPromise = Promise.resolve();
				} else {
					deletingPromise = this.model.destroyRecord();
				}
			}

			deletingPromise.then(onSuccess, onFail).then(resolveWrapper, rejectWrapper);
		});

		return promise;
	}

	// ------------ error handling START --------------

	override validate(onlyFields?: string[]): boolean {
		// if we have BE errors and buffered model isn't changed, just return false
		if (this.model.errors.length && !this.isDirty) {
			return false;
		}

		this.cleanupErrorsIfOnlyGeneral();

		return super.validate(onlyFields);
	}

	cleanupErrorsIfOnlyGeneral(): void {
		// remove general errors from validation
		const errors = this.model.errors as DSErrors & {
			general?: unknown;
			splice: (start: number, deleteCount: number) => void;
		};
		if (Object.keys(errors).length === 1 && errors.general) {
			errors.splice(0, errors.length);
		}
	}

	_updateBackEndErrors(): void {
		const model = this.model;

		if (!model || !isAlive(this)) {
			return;
		}

		const errors = model.errors;

		// errors.messages is much much faster than errors.length / errors.isEmpty
		if (errors && 'get' in errors && errors.messages?.length) {
			errors.forEach((error) => {
				this.errors[error.attribute] = [{ message: error.message }];
			});

			// trigger update for tracked
			this.errors = this.errors;
		} else {
			this.errors = {};
		}
	}

	// ------------ error handling END --------------

	static modelName: string; // defined in model classes that extend this
}
